import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { UNDO_WINDOW_MS } from '@cleartask/shared';

export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastOptions {
  message: string;
  action?: ToastAction;
  /** Padrao: a janela de desfazer da spec 10.3 (8 s). */
  durationMs?: number;
}

interface ToastRecord extends ToastOptions {
  id: number;
}

interface ToastApi {
  showToast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Toasts com acao de desfazer (spec 10.3).
 *
 * A spec troca dialogo de confirmacao por desfazer: perguntar "tem certeza?"
 * antes de toda acao treina o usuario a clicar "sim" sem ler, e ainda cobra um
 * clique de quem acertou. Executar e oferecer a volta cobra apenas de quem
 * errou.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { ...options, id }]);

      const timer = setTimeout(() => dismiss(id), options.durationMs ?? UNDO_WINDOW_MS);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  // Limpa os timers pendentes na desmontagem. Sem isso, um `setToasts` dispara
  // depois que o componente saiu — vazamento silencioso a cada troca de rota.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/*
        `aria-live="polite"` anuncia o toast ao leitor de tela sem interromper
        o que ele estiver lendo (spec 10.5). A regiao fica sempre no DOM: uma
        regiao live inserida junto com o conteudo nao e anunciada.
      */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex flex-col items-center gap-2 px-4 pb-[calc(var(--tabbar-height)+1rem)] md:pb-6"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto flex w-full max-w-sm items-center justify-between gap-4 rounded bg-brand-navy px-4 py-3 text-sm text-white shadow-card"
          >
            <span>{toast.message}</span>

            {toast.action ? (
              <button
                type="button"
                className="min-h-touch shrink-0 rounded-sm px-2 font-medium text-white underline underline-offset-2"
                onClick={() => {
                  toast.action?.onAction();
                  dismiss(toast.id);
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast precisa estar dentro de <ToastProvider>');
  return context;
}
